import { useState, useEffect, useRef } from "react";

/**
 * Custom hook for global text-to-speech functionality
 * Can be used across the entire application
 * 
 * @returns {Object} {
 *   speak: Function to speak text,
 *   stop: Function to stop speaking,
 *   isSpeaking: Boolean indicating if speech is active,
 *   voicesReady: Boolean indicating if voices are loaded,
 *   speechEnabled: Boolean to enable/disable speech,
 *   setSpeechEnabled: Function to toggle speech
 * }
 */
export const useSpeech = () => {
  const [voicesReady, setVoicesReady] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // refs for delayed second utter and timeout cleanup
  const timeoutRef = useRef(null);
  const secondUtterRef = useRef(null);

  // Initialize voices
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        setVoicesReady(true);
      }
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  /**
   * Speak the given text
   * @param {string | Object} text - Text to speak or React element with props.en
   * @param {Object} options - Optional callbacks
   * @param {Function} options.onStart - Called when speech starts
   * @param {Function} options.onEnd - Called when speech ends
   */
  const speak = (text, { onStart, onEnd } = {}) => {
    if (!window.speechSynthesis || !voicesReady || !speechEnabled || !text) return;
console.table(
  window.speechSynthesis.getVoices().map(v => ({
    name: v.name,
    lang: v.lang,
    uri: v.voiceURI
  }))
);

const getPreferredVoice = (langPrefix = "en") => {
  const voices = window.speechSynthesis.getVoices();

  // Priority list for female voices
  const femalePriority = {
    en: [
      "Zira",
      "Samantha",
      "Kendra",
      "Sara",
      "Google UK English Female",
    ],
    hi: [
      "Swara",          // Microsoft Swara (Natural) - Hindi
      "Heera",          // Microsoft Heera (Hindi female - Windows)
      "Google हिन्दी",  // Google Hindi female
      "Hindi Female"
    ]
  };

  const priorityList = femalePriority[langPrefix] || [];

  // 1. Try exact female priority match for language
  let voice = voices.find(v =>
    priorityList.some(name =>
      v.name.toLowerCase().includes(name.toLowerCase())
    ) && v.lang?.startsWith(langPrefix)
  );

  // 2. Fallback: any voice for language
  if (!voice) {
    voice = voices.find(v => v.lang?.startsWith(langPrefix));
  }

  // 3. Last fallback: any voice
  return voice || voices[0];
};


    const enMessage =
      typeof text?.props?.en === "string"
        ? text.props.en
        : typeof text === "string"
        ? text
        : "";

    const hiMessage = typeof text?.props?.hi === "string" ? text.props.hi : null;

    if (!enMessage && !hiMessage) return;

    // Cleanup previous pending actions
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    const createUtter = (message, lang = "en-IN") => {
      const utter = new SpeechSynthesisUtterance(message);
   
       utter.voice = getPreferredVoice(lang.split("-")[0]);

      utter.lang = lang;
      utter.volume = 1;
      utter.rate = 1;
      utter.pitch = 1;
      return utter;
    };

    // Speak English first if available
    if (enMessage) {
      const enUtter = createUtter(enMessage, "en-IN", (v) => v.name.includes("Google") || (v.lang && v.lang.startsWith("en")), { gender: "female" });

      enUtter.onstart = () => {
        setIsSpeaking(true);
        onStart?.();
      };

      enUtter.onend = () => {
        if (hiMessage) {
          // wait ~2.5 seconds before speaking Hindi
          timeoutRef.current = setTimeout(() => {
            const hiUtter = createUtter(hiMessage, "hi-IN");
            secondUtterRef.current = hiUtter;

            hiUtter.onstart = () => setIsSpeaking(true);
            hiUtter.onend = () => {
              setIsSpeaking(false);
              secondUtterRef.current = null;
              onEnd?.();
            };
            hiUtter.onerror = () => {
              setIsSpeaking(false);
              secondUtterRef.current = null;
              onEnd?.();
            };

            window.speechSynthesis.speak(hiUtter);
          }, 2500);
        } else {
          setIsSpeaking(false);
          onEnd?.();
        }
      };

      enUtter.onerror = () => {
        setIsSpeaking(false);
        onEnd?.();
      };

      window.speechSynthesis.speak(enUtter);
      return;
    }

    // If no English available, speak Hindi directly
    if (hiMessage) {
      const hiUtter = createUtter(hiMessage, "hi-IN");
      secondUtterRef.current = hiUtter;

      hiUtter.onstart = () => {
        setIsSpeaking(true);
        onStart?.();
      };
      hiUtter.onend = () => {
        setIsSpeaking(false);
        secondUtterRef.current = null;
        onEnd?.();
      };
      hiUtter.onerror = () => {
        setIsSpeaking(false);
        secondUtterRef.current = null;
        onEnd?.();
      };

      window.speechSynthesis.speak(hiUtter);
    }
  };

  /**
   * Stop current speech
   */
  const stop = () => {
    // clear any pending delayed Hindi playback
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // cancel any active utterances
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    secondUtterRef.current = null;
    setIsSpeaking(false);
  };

  return {
    speak,
    stop,
    isSpeaking,
    voicesReady,
    speechEnabled,
    setSpeechEnabled,
  };
};

export default useSpeech;
